import React from 'react';
import classNames from 'classnames';

import CommonExposure from '$/components/CommonExposure';
import IconFont from '$/components/IconFont';
import type {
  TeamHomeProjectItem,
  V3CanvasProjectItem,
} from '$/domains/workFlowV3/type';
import useCreateRoom from '$/hooks/useCreateRoom';
import { useOfficialStore } from '$/store/useOfficialStore';
import { mtstatTrack } from '$/utils/mtstat';
import { navigate } from '@/common/agents';
import { useCommonStore } from '@/store/useCommonStore';
import useTeamStore from '@/store/useTeamStore';

import { AI_TOOLS } from '@/domains/tools/constant';
import { getLocalizedAiToolRoute } from '@/domains/tools/getLocalizedAiToolRoute';
import { useLanguageStore } from '$/store/languageStore';
import EffectTemplateCard from './EffectTemplateCard';
import ToolCard from './ToolCard';
import type { AIToolItem } from './ToolCard';
import styles from './index.module.scss';

type HomeProjectItem = V3CanvasProjectItem | TeamHomeProjectItem;

type Props = {
  className?: string;
  /** 外部传入的当前个人或团队项目列表 */
  recentProjects?: HomeProjectItem[];
  onToolClick?: (item: AIToolItem) => void;
  onSelfCardClick?: (item: HomeProjectItem) => void;
};

type ToolSectionTitleProps = {
  children: React.ReactNode;
  moreClick?: () => void;
  showMoreButton?: boolean;
};

/**
 * 渲染工具分组标题与右箭头。
 */
function ToolSectionTitle({ children, moreClick, showMoreButton }: ToolSectionTitleProps) {
  return (
    <div className={styles.sectionTitle} onClick={moreClick} style={{ cursor: moreClick ? 'pointer' : 'default' }}>
      <span>{children}</span>
      {showMoreButton && <IconFont name="chevronRightFill" className={styles.moreButton} fontSize={10} />}
    </div>
  );
}

/**
 * 渲染 BaseHomeNew 首屏工具栏。
 */
function HomeToolBar({
  className, recentProjects = [], onToolClick, onSelfCardClick,
}: Props) {
  const navigateMap = useOfficialStore((state) => state.navigateMap);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const isTeam = useTeamStore((state) => Boolean(state.teamInfo?.oid));
  const languageStore = useLanguageStore((state) => state.languageStore);
  const { createRoom } = useCreateRoom({
    onLoginSuccessed: async () => {
      await initUserInfo();
    },
    createAfterLogin: true,
  });

  const personalTool: AIToolItem = {
    id: 'new-scene',
    title: languageStore.smartcatTranslationText((v) => v.create_new_projects),
    description: languageStore.smartcatTranslationText((v) => v.custom_workflow),
    icon: 'plusBlack',
    route: '/--roboneo/team_studio',
  };

  const projectSectionTitle = isTeam
    ? languageStore.smartcatTranslationText((v) => v.team_project)
    : languageStore.smartcatTranslationText((v) => v.my_projects);
  const untitledProjectText = languageStore.smartcatTranslationText((v) => v.untitled_project);

  /** 首页项目区域最多展示两个项目。 */
  const visibleProjects = recentProjects.slice(0, 2);

  /**
   * 新建 AgentTeam 房间并跳转。
   */
  const handleCreateScene = () => {
    createRoom({
      trackLocation: isTeam ? 'team_add' : 'home_personal_space',
      trackType: null,
    });
  };

  /** 点击已有项目卡片，携带项目和默认房间标识进入工作室。 */
  const handleProjectClick = (project: HomeProjectItem) => {
    const roomId = String(project.room_id || project.project_id || '');
    const projectId = String(project.project_id || '');
    if (!roomId) return;
    mtstatTrack('roboneo_personal_space_click', {
      room_id: roomId,
      cover_url: project.cover_url || '',
    });
    navigateMap.historyTeamFlow({
      room_id: roomId,
      ...(projectId ? { project_id: projectId } : {}),
    });
    onSelfCardClick?.(project);
  };

  /** 首页项目卡片曝光埋点。 */
  const handleProjectExpose = (project: HomeProjectItem) => {
    const roomId = String(project.room_id || project.project_id || '');
    mtstatTrack('roboneo_personal_space_show', {
      room_id: roomId,
      cover_url: project.cover_url || '',
    });
  };

  /**
   * 点击 AI 工具时按当前语言进入 Essio 对应落地页。
   */
  const handleToolClick = (item: AIToolItem) => {
    mtstatTrack('roboneo_aifunc_enter', {
      func: item.id,
      source: 'home_page',
    });
    navigate({
      mode: 'router',
      pathname: getLocalizedAiToolRoute(item.route, languageStore.languageCode) as any,
    });
    onToolClick?.(item);
  };

  const handleSelfMoreClick = () => {
    navigate({
      mode: 'router',
      pathname: isTeam ? '/--roboneo/team_project' : '/--roboneo/project',
    });
  };

  return (
    <div className={classNames(styles.homeToolBar, className)}>
      <div className={styles.personalSection}>
        <ToolSectionTitle showMoreButton moreClick={handleSelfMoreClick}>{projectSectionTitle}</ToolSectionTitle>
        <div
          className={classNames(styles.personalContent, {
            [styles.personalContentLoggedIn]: isUserLoggedIn,
          })}
        >
          <ToolCard item={personalTool} fixedWidth onClick={handleCreateScene} />
          {visibleProjects.map((project, index) => (
            <CommonExposure
              key={project.project_id}
              onExposed={() => handleProjectExpose(project)}
            >
              <ToolCard
                item={{
                  id: String(project.project_id),
                  title: project.name?.trim()
                    || ('room_title' in project ? project.room_title?.trim() : '')
                    || untitledProjectText,
                  description: '',
                  icon: '',
                  route: '/--roboneo/team_studio',
                  url: project.cover_url,
                }}
                fixedWidth
                onClick={() => handleProjectClick(project)}
                hasMaskButton
                // 首屏首张封面卡片是 LCP 候选，用高优先级加载封面图。
                priority={index === 0}
              />
            </CommonExposure>

          ))}
        </div>
      </div>

      <div className={styles.creativeSection}>
        <ToolSectionTitle>{languageStore.smartcatTranslationText((v) => v.ai_create_tools)}</ToolSectionTitle>
        <div className={styles.creativeContent}>
          {/* 特效模板组件 */}
          <EffectTemplateCard />
          {/* 工具卡片列表 */}
          {AI_TOOLS.map((item: any) => (
            <ToolCard key={item.id} item={item} onClick={() => handleToolClick(item)} useTranslation />
          ))}
        </div>
      </div>
    </div>
  );
}

export default React.memo(HomeToolBar);
